<template>
  <div>
    <div class="d-flex align-center mb-4">
      <span class="text-h6">Members</span>
      <v-spacer />
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-account-plus"
        data-cy="add-member-btn"
        @click="showAddDialog = true"
      >
        Add Member
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="members"
      :items-per-page="-1"
      :loading="loading"
      hide-default-footer
    >
      <template #item.user="{ item }">
        <div>
          <div class="font-weight-medium">{{ item.user ? `${item.user.firstname} ${item.user.lastname}` : '—' }}</div>
          <div class="text-caption text-disabled">{{ item.email }}</div>
        </div>
      </template>
      <template #item.role="{ item }">
        <v-select
          :model-value="item.role"
          :items="roleOptions"
          item-title="label"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 140px"
          :disabled="isLastOwner(item)"
          @update:model-value="(role) => onRoleChange(item, role)"
        />
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon="mdi-delete-outline"
          variant="text"
          size="small"
          color="error"
          :disabled="isLastOwner(item)"
          :title="isLastOwner(item) ? 'An org must keep at least one owner' : 'Remove member'"
          data-cy="remove-member-btn"
          @click="onRemove(item)"
        />
      </template>
      <template v-slot:bottom></template>
    </v-data-table>

    <v-dialog v-model="showAddDialog" max-width="420px">
      <v-card>
        <v-card-title class="pa-4">Add Member</v-card-title>
        <v-card-text class="pt-0">
          <v-form ref="addForm" v-model="addValid" @submit.prevent="onAdd">
            <v-text-field
              v-model="newEmail"
              label="Email address"
              variant="outlined"
              density="default"
              data-cy="invite-email-input"
              :rules="[rules.required, rules.email]"
              validate-on="input"
              autofocus
              @keyup.enter="onAdd"
            />
            <v-select
              v-model="newRole"
              :items="roleOptions"
              item-title="label"
              item-value="value"
              label="Role"
              variant="outlined"
              density="default"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-end pa-4 pt-0">
          <v-btn variant="outlined" @click="showAddDialog = false">Cancel</v-btn>
          <v-btn variant="flat" color="primary" :loading="adding" :disabled="!addValid" data-cy="invite-submit-btn" @click="onAdd">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  orgId: { type: String, required: true }
})

const store = useStore()

const members = computed(() => store.getters['orgMembers/getMembersByOrgId'](props.orgId))

// An org must always keep at least one OWNER — so the sole remaining owner's
// role and remove action are locked (for self AND others). This both enables
// ownership transfer (promote someone, then step away) and prevents orphaning.
const ownerCount = computed(() => members.value.filter((m) => m.role === 'OWNER').length)
const isLastOwner = (item) => item.role === 'OWNER' && ownerCount.value === 1

const loading = ref(false)
const adding = ref(false)
const showAddDialog = ref(false)
const addForm = ref(null)
const addValid = ref(false)
const newEmail = ref('')
const newRole = ref('MEMBER')

const headers = [
  { title: 'User', key: 'user', sortable: false },
  { title: 'Role', key: 'role', sortable: false, width: '160px' },
  { title: '', key: 'actions', sortable: false, width: '48px' }
]

const roleOptions = [
  { label: 'Owner', value: 'OWNER' },
  { label: 'Member', value: 'MEMBER' },
  { label: 'Viewer', value: 'VIEWER' }
]

const rules = {
  required: v => !!v || 'Required',
  email: v => /.+@.+\..+/.test(v) || 'Enter a valid email'
}

const onRoleChange = async (item, role) => {
  await store.dispatch('orgMembers/updateOrgMember', { id: item.id, orgId: props.orgId, role })
}

const onRemove = async (item) => {
  await store.dispatch('orgMembers/deleteOrgMember', { id: item.id, orgId: props.orgId, userId: item.userId })
}

const onAdd = async () => {
  // Guard even though the button is disabled when invalid — Enter can still fire onAdd.
  if (!addValid.value) {
    return
  }
  adding.value = true
  const result = await store.dispatch('orgMembers/createOrgMember', {
    orgId: props.orgId,
    email: newEmail.value,
    role: newRole.value
  })
  adding.value = false
  if (result) {
    showAddDialog.value = false
    newEmail.value = ''
    newRole.value = 'MEMBER'
    addForm.value?.resetValidation()
  }
}
</script>
